import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  schema?: object;
  image?: string;
}

const SEOHandler: React.FC<SEOProps> = ({ title, description, canonical, schema, image }) => {
  const location = useLocation();
  const DOMAIN = 'https://planstack.com.au';

  useEffect(() => {
    // 1. Update Document Title
    document.title = title;

    // 2. Manage Canonical URL
    // Special handling for homepage to avoid "Page with redirect" issues in GSC
    // Google prefers the clean URL without the hash for the root domain.
    let targetCanonical = canonical;
    if (!targetCanonical) {
      if (location.pathname === '/' && (window.location.hash === '' || window.location.hash === '#/')) {
        targetCanonical = `${DOMAIN}/`;
      } else {
        // For subpages, we must include the hash for indexing to work with HashRouter
        targetCanonical = `${DOMAIN}/#${location.pathname}${location.search}`;
      }
    }

    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (linkCanonical) {
      linkCanonical.setAttribute('href', targetCanonical);
    } else {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      linkCanonical.setAttribute('href', targetCanonical);
      document.head.appendChild(linkCanonical);
    }

    // 3. Update Meta Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }

    // 4. Update Open Graph Tags
    const updateMetaTag = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    };

    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:url', targetCanonical);
    if (image) updateMetaTag('og:image', image);

    // 5. Update Twitter Tags
    const updateTwitterTag = (name: string, content: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    };

    updateTwitterTag('twitter:title', title);
    updateTwitterTag('twitter:description', description);
    if (image) updateTwitterTag('twitter:image', image);

    // 6. JSON-LD Schema Injection
    if (schema) {
      const existingScript = document.getElementById('seo-schema');
      if (existingScript) {
        existingScript.textContent = JSON.stringify(schema);
      } else {
        const script = document.createElement('script');
        script.id = 'seo-schema';
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      }
    }

    return () => {
      // Cleanup dynamic schema on unmount
      const schemaScript = document.getElementById('seo-schema');
      if (schemaScript) schemaScript.remove();
    };
  }, [title, description, canonical, schema, image, location]);

  return null;
};

export default SEOHandler;